import React, { useState, useEffect } from "react";
import "../styles/ManageInventory.css";

function ManageInventory() {
  const [products, setProducts] = useState([]);
  const [inventoryRecords, setInventoryRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    products: []
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchProducts();
    fetchInventoryRecords();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(
        "https://cairomed-backend.vercel.app/api/products/getProduct"
      );
      if (!response.ok) throw new Error("Failed to fetch products");
      const data = await response.json();
      setProducts(data);
      
      // Initialize form data with all products and count 0
      if (!isEditing) {
        setFormData({
          products: data.map(product => ({
            product: product._id,
            count: 0,
            productData: product
          }))
        });
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchInventoryRecords = async () => {
    try {
      console.log('Fetching inventory records...');
      const response = await fetch(
        "https://cairomed-backend.vercel.app/api/inventory?page=1&limit=100"
      );
      if (!response.ok) throw new Error("Failed to fetch inventory records");
      const data = await response.json();
      console.log('Full API Response:', data);
      setInventoryRecords(data.data?.inventory || []);
    } catch (error) {
      console.error("Error fetching inventory records:", error);
    }
  };

  const handleProductCountChange = (productId, count) => {
    const updatedProducts = formData.products.map(item => 
      item.product === productId ? { ...item, count: count } : item
    );
    setFormData({ ...formData, products: updatedProducts });
  };

  const resetForm = () => {
    // Reset to all products with count 0
    setFormData({
      products: products.map(product => ({
        product: product._id,
        count: 0,
        productData: product
      }))
    });
    setIsEditing(false);
    setSelectedRecord(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Filter products with count > 0
    const productsToSubmit = formData.products.filter(item => item.count > 0);
    
    // Validate form
    if (productsToSubmit.length === 0) {
      alert("Please add at least one product with count greater than 0");
      return;
    }

    try {
      const inventoryData = {
        Products: productsToSubmit.map(item => ({
          ProductID: item.product,
          Count: item.count
        }))
      };

      let response;
      if (isEditing && selectedRecord) {
        response = await fetch(
          `https://cairomed-backend.vercel.app/api/inventory/${selectedRecord._id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(inventoryData),
          }
        );
      } else {
        response = await fetch(
          "https://cairomed-backend.vercel.app/api/inventory",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(inventoryData),
          }
        );
      }

      if (!response.ok) {
        throw new Error(isEditing ? "Failed to update record" : "Failed to create record");
      }

      resetForm();
      fetchInventoryRecords();
      alert(isEditing ? "Record updated successfully!" : "Record created successfully!");
    } catch (error) {
      console.error("Error:", error);
      alert(isEditing ? "Failed to update record" : "Failed to create record");
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        const response = await fetch(
          `https://cairomed-backend.vercel.app/api/inventory/${recordId}`,
          {
            method: "DELETE",
          }
        );

        if (!response.ok) throw new Error("Failed to delete record");

        fetchInventoryRecords();
        alert("Record deleted successfully!");
      } catch (error) {
        console.error("Error deleting record:", error);
        alert("Failed to delete record");
      }
    }
  };

  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    
    // Map existing products with their counts from the record
    const editedProducts = products.map(product => {
      let existingProduct = null;
      
      // Find the matching product by comparing IDs
      for (const p of record.Products) {
        const productId = typeof p.ProductID === 'object' ? 
                          (p.ProductID._id || p.ProductID.id) : 
                          p.ProductID;
        
        if (productId === product._id) {
          existingProduct = p;
          break;
        }
      }
      
      return {
        product: product._id,
        count: existingProduct ? existingProduct.Count : 0,
        productData: product
      };
    });
    
    setFormData({
      products: editedProducts
    });
    
    setIsEditing(true);
    document.getElementById("inventory-form").scrollIntoView({ behavior: "smooth" });
  };

  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRecord(null);
  };

  return (
    <div className="manage-inventory-container">
      <div className="inventory-form-container container" id="inventory-form">
        <h3>{isEditing ? "Edit Inventory Record" : "Create New Inventory Record"}</h3>
        <form onSubmit={handleSubmit} className="row w-100 px-0 g-4 pt-3 pb-5">
          <div className="products-section">
            <h4>Products</h4>
            <div className="products-grid">
              {formData.products.map((item) => {
                const product = item.productData;
                return (
                  <div key={item.product} className="product-card">
                    <div className="product-image">
                      {product.ProductImg ? (
                        <img 
                          src={product.ProductImg} 
                          alt={product.ProductNameEN} 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://via.placeholder.com/150";
                          }}
                        />
                      ) : (
                        <img src={product.Image} alt="Product placeholder" />
                      )}
                    </div>
                    <div className="product-info">
                      <h5>{product.ProductNameEN}</h5>
                      <p>{product.ProductNameAR}</p>
                      <div className="count-input">
                        <label>Count:</label>
                        <input
                          type="number"
                          min="0"
                          value={item.count}
                          onChange={(e) => handleProductCountChange(item.product, parseInt(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="d-flex gap-2 mt-4">
            <button type="submit" className="submit-btn">
              {isEditing ? "Update Record" : "Create Record"}
            </button>
            {isEditing && (
              <button
                type="button"
                className="cancel-btn"
                onClick={resetForm}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="inventory-history">
        <h3>Inventory History</h3>
        {inventoryRecords.length > 0 ? (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {inventoryRecords.map((record) => (
                  <tr key={record._id}>
                    <td>
                      {new Date(record.DateOfGard).toLocaleDateString()}{' '}
                      {new Date(record.DateOfGard).toLocaleTimeString()}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleViewRecord(record)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleEditRecord(record)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteRecord(record._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No inventory records found.</p>
        )}
      </div>

      {/* View Record Modal */}
      {showModal && selectedRecord && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Inventory Record Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <p>
                      <strong>Date:</strong>{" "}
                      {new Date(selectedRecord.DateOfGard).toLocaleDateString()}{' '}
                      {new Date(selectedRecord.DateOfGard).toLocaleTimeString()}
                    </p>
                  </div>
                </div>

                <h6>Products:</h6>
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRecord.Products.map((item, i) => {
                      const product = products.find(p => p._id === item.ProductID);
                      const name = product
                        ? `${product.ProductNameEN} - ${product.ProductNameAR}`
                        : item.ProductID.ProductNameEN || "Unknown Product";
                      return (
                        <tr key={i}>
                          <td>{name}</td>
                          <td>{item.Count}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => window.print()}
                >
                  Print Record
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageInventory; 